import type {
  Action,
  ActionOutcome,
  ActionType,
  AgentId,
  BehaviouralCounters,
  ItemKind,
  MatchConfig,
  Observation,
  ResourceKind,
  ResourceView,
  SimEvent,
  Vec2,
} from "@cc/protocol";
import { distance, TICKS_PER_SECOND } from "@cc/protocol";
import {
  ARENA,
  RESOURCE_TABLE,
  SHIELD_HEAL,
  TRAP_BAIT_VALUE,
  clampToArena,
  decisionPhase,
  rankingTotal,
  spawnPositions,
} from "./config.js";
import { Rng } from "./rng.js";

/* ------------------------------------------------------------------ */
/* Internal state                                                      */
/* ------------------------------------------------------------------ */

interface ResourceState {
  id: string;
  kind: ResourceKind;
  value: number;
  position: Vec2;
  spawnedAt: number;
  /** Trap that has been spotted; no longer visually disguises itself. */
  revealed: boolean;
}

/**
 * A resource as remembered from a previous tick. Deliberately excludes
 * `distance` and `inRange` because those are properties of the *current*
 * observation and would be misleading if carried over from an older sighting.
 */
export type RememberedResource = Omit<ResourceView, "distance" | "inRange">;

interface CricketState {
  id: AgentId;
  slot: number;
  position: Vec2;
  spawn: Vec2;
  hp: number;
  score: number;
  resourcesCollected: number;
  alive: boolean;
  eliminated: boolean;
  currentAction: ActionType;
  moveTarget: Vec2 | null;
  isDefending: boolean;
  defendingUntil: number;
  collectingId: string | null;
  collectCompleteAt: number;
  attackReadyAt: number;
  knockbackUntil: number;
  knockbackFrom: Vec2;
  /** Speed multiplier while knockback / flee is active (applied in integrate). */
  knockbackSpeedMul: number;
  fleeUntil: number;
  bladeUntil: number;
  bombs: number;
  blades: number;
  seenOpponent: boolean;
  lastOpponentPosition: Vec2 | null;
  knownTraps: Set<string>;
  memory: Map<string, { view: RememberedResource; tick: number }>;
  /** Most recent action result visible on the next observe(). */
  lastActionOutcome: ActionOutcome | null;
  behavioural: BehaviouralCounters;
}

export interface RenderCricket {
  id: AgentId;
  slot: number;
  position: Vec2;
  hp: number;
  maxHp: number;
  score: number;
  totalScore: number;
  alive: boolean;
  defending: boolean;
  collecting: boolean;
  fleeing: boolean;
  bladeActive: boolean;
  bombs: number;
  blades: number;
  action: ActionType;
}

export interface RenderResource {
  id: string;
  /**
   * When `revealTraps` is false this is the *believed* kind, so the UI cannot
   * spoil the drama. Spectators usually prefer revealTraps=true, because
   * watching a cricket walk into a trap it cannot see is the whole point.
   */
  kind: ResourceKind;
  value: number;
  position: Vec2;
  revealed: boolean;
}

export interface RenderState {
  tick: number;
  timeRemainingTicks: number;
  finished: boolean;
  crickets: readonly RenderCricket[];
  resources: readonly RenderResource[];
}

export interface SnapshotOptions {
  revealTraps?: boolean;
}

function emptyCounters(): BehaviouralCounters {
  return {
    decisions: 0,
    moves: 0,
    attacksAttempted: 0,
    attacksLanded: 0,
    collectionsAttempted: 0,
    collectionsCompleted: 0,
    collectionsFailed: 0,
    trapsSteppedOn: 0,
    damageDealt: 0,
    damageTaken: 0,
    distanceTravelled: 0,
    maxExplorationDistance: 0,
    scoutActions: 0,
    trapsDetected: 0,
    resourceFocusSum: 0,
    resourceFocusSamples: 0,
    retreatTicks: 0,
  };
}

/**
 * The match simulation.
 *
 * Determinism contract: for a given `MatchConfig`, the sequence of actions
 * submitted via `submit()` fully determines the outcome. No wall-clock time,
 * no Math.random, no iteration-order dependence on Map/Set insertion of
 * external data. Replaying a recorded action log against the same seed
 * reproduces every position to the last bit.
 *
 * Tick order (fixed, documented, tested):
 *   1. resource spawn / expiry
 *   2. movement integration (incl. knockback / flee and channeled COLLECT/SCOUT)
 *   3. COLLECT channel completion
 *   4. spinning-blade aura ticks
 *   5. passive trap detection / landmine contact
 *   6. elimination / end-of-match checks
 */
export class Simulation {
  readonly config: MatchConfig;
  readonly events: SimEvent[] = [];

  private rng: Rng;
  private _tick = 0;
  private _finished = false;
  private winner: string | "draw" = "draw";
  private winnerReason: "score" | "elimination" | "timeout" | "draw" = "draw";
  private seq = 0;
  private resources: ResourceState[] = [];
  private resourceSeq = 0;
  private crickets: CricketState[] = [];
  private phases: number[];
  private nextResourceId = 1;

  constructor(config: MatchConfig) {
    this.config = config;
    this.rng = new Rng(config.seed);
    this.phases = config.agentIds.map((_, slot) =>
      decisionPhase(config.seed, slot, config.decisionIntervalTicks, {
        aligned: config.alignedDecisions === true,
      }),
    );
    const spawns = spawnPositions(config.arenaSize);
    this.crickets = config.agentIds.map((id, slot) =>
      this.makeCricket(id, slot, spawns[slot]!),
    );
    this.emit("match:start", {
      seed: config.seed,
      arenaSize: config.arenaSize,
      durationTicks: config.durationTicks,
      agentIds: [...config.agentIds],
    });
    this.spawnInitialResources();
  }

  private makeCricket(id: AgentId, slot: number, spawn: Vec2): CricketState {
    return {
      id,
      slot,
      position: spawn,
      spawn,
      hp: this.config.stats.maxHp,
      score: 0,
      resourcesCollected: 0,
      alive: true,
      eliminated: false,
      currentAction: "WAIT",
      moveTarget: null,
      isDefending: false,
      defendingUntil: -1,
      collectingId: null,
      collectCompleteAt: -1,
      attackReadyAt: -1,
      knockbackUntil: -1,
      knockbackFrom: spawn,
      knockbackSpeedMul: this.config.stats.knockbackSpeedMul,
      fleeUntil: -1,
      bladeUntil: -1,
      bombs: 0,
      blades: 0,
      seenOpponent: false,
      lastOpponentPosition: null,
      knownTraps: new Set(),
      memory: new Map(),
      lastActionOutcome: null,
      behavioural: emptyCounters(),
    };
  }

  get tick(): number {
    return this._tick;
  }

  get finished(): boolean {
    return this._finished;
  }

  get timeRemainingTicks(): number {
    return Math.max(0, this.config.durationTicks - this._tick);
  }

  private cricket(slot: number): CricketState {
    return this.crickets[slot]!;
  }

  private emit(type: SimEvent["type"], data: Record<string, unknown>, agentId?: AgentId): void {
    const ev: SimEvent = {
      seq: this.seq++,
      tick: this._tick,
      type,
      ...(agentId === undefined ? {} : { agentId }),
      data,
    };
    this.events.push(ev);
  }

  /* ---------------------------------------------------------------- */
  /* Resource lifecycle                                                */
  /* ---------------------------------------------------------------- */

  private randomResourcePosition(): Vec2 {
    const m = ARENA.margin + 4;
    const hi = this.config.arenaSize - m;
    return [this.rng.range(m, hi), this.rng.range(m, hi)];
  }

  private spawnResource(): void {
    // Traps are held back until the match has developed, and stop spawning late
    // so the final minute is a clean fight rather than a minefield.
    const trapsAvailable =
      this._tick >= ARENA.trapUnlockTick &&
      this._tick < this.config.durationTicks - ARENA.trapCutoffTicks;

    const table = RESOURCE_TABLE.filter((t) => t.kind !== "trap" || trapsAvailable);
    const template = this.rng.weighted(table.map((t) => [t, t.weight] as const));
    const isTrap = template.kind === "trap";
    const value = isTrap ? TRAP_BAIT_VALUE : template.value;
    const spawn = this.randomResourcePosition();
    const resource: ResourceState = {
      id: `r${this.nextResourceId++}`,
      kind: template.kind,
      value,
      position: spawn,
      spawnedAt: this._tick,
      revealed: false,
    };
    this.resources.push(resource);
    this.emit("resource:spawn", {
      resourceId: resource.id,
      kind: resource.kind,
      value: resource.value,
      position: resource.position,
    });
  }

  private spawnInitialResources(): void {
    for (let i = 0; i < 5; i++) this.spawnResource();
  }

  private maintainResources(): void {
    // Expire stale resources so the map keeps churning.
    const expiredIds = new Set<string>();
    this.resources = this.resources.filter((r) => {
      const stale = this._tick - r.spawnedAt > ARENA.resourceLifetimeTicks;
      if (stale) expiredIds.add(r.id);
      return !stale;
    });

    if (
      this._tick % ARENA.spawnIntervalTicks === 0 &&
      this.resources.length < ARENA.maxResources
    ) {
      this.spawnResource();
    }

    // Forget pointers to expired resources so agents do not chase ghosts.
    for (const cricket of this.crickets) {
      if (cricket.collectingId && expiredIds.has(cricket.collectingId)) {
        cricket.collectingId = null;
      }
      for (const id of expiredIds) cricket.memory.delete(id);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Observation                                                       */
  /* ---------------------------------------------------------------- */

  /**
   * Builds the information set for one cricket. This is the *only* channel
   * through which an agent learns about the world, which is what keeps the
   * information-asymmetry design honest: an agent cannot perceive anything
   * outside its vision radius, no matter how smart it is.
   */
  observe(slot: number): Observation {
    const self = this.cricket(slot);
    const opponent = this.cricket(1 - slot);
    const stats = this.config.stats;

    const resources: ResourceView[] = [];
    for (const r of this.resources) {
      const d = distance(self.position, r.position);
      if (d > stats.visionRadius) continue;
      const identified = r.kind === "trap" && (self.knownTraps.has(r.id) || r.revealed);
      const view: ResourceView = {
        id: r.id,
        kind: identified ? "trap" : r.kind,
        position: r.position,
        value: r.value,
        identifiedAsTrap: identified,
        inRange: d <= stats.collectRange,
        distance: d,
      };
      resources.push(view);
      // Remember the sighting, but strip the tick-specific fields so a stale
      // memory can never masquerade as current knowledge.
      const { distance: _d, inRange: _r, ...remembered } = view;
      self.memory.set(r.id, { view: remembered, tick: this._tick });
    }

    const visibleIds = new Set(resources.map((r) => r.id));
    const lastKnownResources: ResourceView[] = [];
    for (const [id, entry] of self.memory) {
      if (visibleIds.has(id)) continue;
      lastKnownResources.push({ ...entry.view, distance: 0, inRange: false });
    }

    const opponentDistance = distance(self.position, opponent.position);
    const opponentVisible = opponent.alive && opponentDistance <= stats.visionRadius;
    if (opponentVisible) {
      self.seenOpponent = true;
      self.lastOpponentPosition = opponent.position;
    }

    return {
      tick: this._tick,
      timeRemainingTicks: this.timeRemainingTicks,
      arenaSize: this.config.arenaSize,
      self: {
        id: self.id,
        position: self.position,
        hp: self.hp,
        maxHp: stats.maxHp,
        score: self.score,
        totalScore: rankingTotal(self.score, self.hp),
        collecting: self.collectingId !== null,
        defending: self.isDefending,
        fleeing: self.fleeUntil > this._tick,
        bladeActive: self.bladeUntil > this._tick,
        attackReadyAt: self.attackReadyAt,
        knockedBackUntil: self.knockbackUntil,
        totalCollected: self.resourcesCollected,
        failedCollections: self.behavioural.collectionsFailed,
        bombs: self.bombs,
        blades: self.blades,
      },
      opponent: opponentVisible
        ? {
            id: opponent.id,
            visible: true,
            position: opponent.position,
            hp: opponent.hp,
            score: opponent.score,
            distance: opponentDistance,
          }
        : { id: opponent.id, visible: false },
      resources,
      knownTraps: [...self.knownTraps],
      lastKnownResources,
      hasSeenOpponent: self.seenOpponent,
      ...(self.lastOpponentPosition === null
        ? {}
        : { lastOpponentPosition: self.lastOpponentPosition }),
      ...(self.lastActionOutcome === null
        ? {}
        : { lastActionOutcome: self.lastActionOutcome }),
    };
  }

  /* ---------------------------------------------------------------- */
  /* Action intake                                                     */
  /* ---------------------------------------------------------------- */

  isDecisionDue(slot: number): boolean {
    if (this._finished) return false;
    const interval = this.config.decisionIntervalTicks;
    if (interval <= 1) return true;
    return (this._tick + this.phases[slot]!) % interval === 0;
  }

  /**
   * Registers an agent's intent. Called by the match driver after `observe()`.
   * The action's effect is applied during the next `step()`.
   */
  submit(slot: number, action: Action): void {
    const self = this.cricket(slot);
    if (!self.alive || this._finished) return;

    self.currentAction = action.action;
    self.behavioural.decisions++;

    if (action.reason) {
      this.emit(
        "agent:decision",
        { action: action.action, reason: action.reason },
        self.id,
      );
    } else {
      this.emit("agent:decision", { action: action.action }, self.id);
    }

    switch (action.action) {
      case "MOVE": {
        self.moveTarget = clampToArena(action.target, this.config.arenaSize, ARENA.margin);
        self.behavioural.moves++;
        break;
      }
      case "SCOUT": {
        self.moveTarget = null;
        self.collectCompleteAt = this._tick + ARENA.scoutTicks;
        self.collectingId = null;
        // Scout pulse is applied immediately; it is the payoff for the pause.
        self.behavioural.scoutActions++;
        this.applyScout(self, action.target);
        break;
      }
      case "COLLECT": {
        this.resolveCollect(self, action.resourceId);
        break;
      }
      case "ATTACK": {
        self.moveTarget = null;
        this.resolveAttack(self, this.cricket(1 - slot), action.targetId);
        break;
      }
      case "DEFEND": {
        self.moveTarget = null;
        const dur = Math.min(30, Math.max(1, action.durationTicks ?? 10));
        self.isDefending = true;
        self.defendingUntil = this._tick + dur;
        this.emit("agent:defend", { durationTicks: dur }, self.id);
        break;
      }
      case "FLEE": {
        this.resolveFlee(self, this.cricket(1 - slot));
        break;
      }
      case "EXCHANGE": {
        self.moveTarget = null;
        this.resolveExchange(self, action.item);
        break;
      }
      case "USE": {
        self.moveTarget = null;
        this.resolveUse(self, this.cricket(1 - slot), action.item);
        break;
      }
      case "WAIT": {
        self.moveTarget = null;
        break;
      }
    }
  }

  private pickCollectTarget(self: CricketState, resourceId?: string): ResourceState | null {
    const range = this.config.stats.collectRange;
    if (resourceId) {
      const found = this.resources.find((r) => r.id === resourceId);
      if (found && distance(self.position, found.position) <= range) return found;
      return null;
    }
    let best: ResourceState | null = null;
    let bestScore = -Infinity;
    for (const r of this.resources) {
      const d = distance(self.position, r.position);
      if (d > range) continue;
      // Agents that have identified a trap will not choose it unaided.
      if (r.kind === "trap" && self.knownTraps.has(r.id)) continue;
      const score = r.value - d;
      if (score > bestScore) {
        bestScore = score;
        best = r;
      }
    }
    return best;
  }

  /**
   * COLLECT is a channelled grab. Failures must be explicit: models otherwise
   * re-issue COLLECT forever while standing still (moveTarget was cleared).
   *
   * On out-of-range / unknown-id, we do *not* freeze the cricket — if a named
   * resource still exists we walk toward it so the match keeps moving.
   */
  private resolveCollect(self: CricketState, resourceId?: string): void {
    const range = this.config.stats.collectRange;
    const ticks = this.config.stats.collectTicks;

    if (self.collectingId !== null) {
      this.setActionOutcome(self, {
        action: "COLLECT",
        ok: true,
        code: "already-channeling",
        detail: `Already channelling COLLECT on ${self.collectingId} — stand still until it banks.`,
        resourceId: self.collectingId,
      });
      return;
    }

    if (resourceId) {
      const found = this.resources.find((r) => r.id === resourceId);
      if (!found) {
        self.behavioural.collectionsFailed++;
        this.setActionOutcome(self, {
          action: "COLLECT",
          ok: false,
          code: "unknown-id",
          detail: `COLLECT failed: resource ${resourceId} is gone or was never real. Pick an id from RESOURCES IN SIGHT, or MOVE.`,
          resourceId,
        });
        this.emit(
          "agent:collect:miss",
          { reason: "unknown-id", resourceId },
          self.id,
        );
        return;
      }
      const d = distance(self.position, found.position);
      if (d > range) {
        self.behavioural.collectionsFailed++;
        // Keep approaching instead of freezing on a failed grab.
        self.moveTarget = clampToArena(found.position, this.config.arenaSize, ARENA.margin);
        this.setActionOutcome(self, {
          action: "COLLECT",
          ok: false,
          code: "out-of-range",
          detail: `COLLECT ${resourceId} failed: too far (dist=${d.toFixed(1)}, need ≤${range}). MOVE onto it, then COLLECT when IN RANGE.`,
          resourceId,
          distance: d,
        });
        this.emit(
          "agent:collect:miss",
          { reason: "out-of-range", resourceId, distance: d, collectRange: range },
          self.id,
        );
        return;
      }
      self.moveTarget = null;
      self.collectingId = found.id;
      self.collectCompleteAt = this._tick + ticks;
      self.behavioural.collectionsAttempted++;
      this.setActionOutcome(self, {
        action: "COLLECT",
        ok: true,
        code: "channel-started",
        detail: `COLLECT ${found.id} channel started — stand still ${ticks} ticks to bank it.`,
        resourceId: found.id,
        distance: d,
      });
      this.emit("agent:collect:start", { resourceId: found.id }, self.id);
      return;
    }

    const nearest = this.pickCollectTarget(self);
    if (!nearest) {
      self.behavioural.collectionsFailed++;
      this.setActionOutcome(self, {
        action: "COLLECT",
        ok: false,
        code: "none-in-range",
        detail: `COLLECT failed: no resource within ${range} units of your feet. MOVE to a crystal's coordinates first.`,
      });
      this.emit("agent:collect:miss", { reason: "none-in-range" }, self.id);
      return;
    }

    const d = distance(self.position, nearest.position);
    self.moveTarget = null;
    self.collectingId = nearest.id;
    self.collectCompleteAt = this._tick + ticks;
    self.behavioural.collectionsAttempted++;
    this.setActionOutcome(self, {
      action: "COLLECT",
      ok: true,
      code: "channel-started",
      detail: `COLLECT ${nearest.id} channel started — stand still ${ticks} ticks to bank it.`,
      resourceId: nearest.id,
      distance: d,
    });
    this.emit("agent:collect:start", { resourceId: nearest.id }, self.id);
  }

  private setActionOutcome(
    self: CricketState,
    outcome: Omit<ActionOutcome, "tick">,
  ): void {
    self.lastActionOutcome = { tick: this._tick, ...outcome };
  }

  /** Clears an active COLLECT channel and records why, for the next observation. */
  private interruptCollect(self: CricketState, via: string): void {
    if (self.collectingId === null) return;
    const resourceId = self.collectingId;
    this.emit(
      "agent:collect:interrupted",
      { resourceId, position: self.position, via },
      self.id,
    );
    self.collectingId = null;
    self.behavioural.collectionsFailed++;
    this.setActionOutcome(self, {
      action: "COLLECT",
      ok: false,
      code: "interrupted",
      detail: `COLLECT ${resourceId} interrupted (${via}) — channel lost, try again when safe.`,
      resourceId,
    });
  }

  private applyScout(self: CricketState, center: Vec2): void {
    let found = 0;
    for (const r of this.resources) {
      if (distance(center, r.position) > ARENA.scoutRadius) continue;
      found++;
      if (r.kind === "trap" && !self.knownTraps.has(r.id)) {
        self.knownTraps.add(r.id);
        r.revealed = true;
        self.behavioural.trapsDetected++;
        this.emit("trap:detected", { resourceId: r.id, via: "scout" }, self.id);
        this.emit("resource:identified", { resourceId: r.id, kind: "trap" }, self.id);
      }
    }
    this.emit("agent:scouted", { center, resourcesFound: found }, self.id);
  }

  /* ---------------------------------------------------------------- */
  /* Combat                                                            */
  /* ---------------------------------------------------------------- */

  private resolveAttack(attacker: CricketState, defender: CricketState, targetId?: AgentId): void {
    attacker.behavioural.attacksAttempted++;
    if (targetId !== undefined && targetId !== defender.id) {
      this.setActionOutcome(attacker, {
        action: "ATTACK",
        ok: false,
        code: "invalid-target",
        detail: `ATTACK failed: unknown targetId. Use the ENEMY line id exactly.`,
      });
      this.emit("agent:attack:miss", { reason: "invalid-target" }, attacker.id);
      return;
    }
    if (!defender.alive) {
      this.setActionOutcome(attacker, {
        action: "ATTACK",
        ok: false,
        code: "no-target",
        detail: `ATTACK failed: enemy is already down.`,
      });
      this.emit("agent:attack:miss", { reason: "no-target" }, attacker.id);
      return;
    }
    if (this._tick < attacker.attackReadyAt) {
      this.setActionOutcome(attacker, {
        action: "ATTACK",
        ok: false,
        code: "cooldown",
        detail: `ATTACK failed: still on cooldown until tick ${attacker.attackReadyAt}.`,
      });
      this.emit("agent:attack:miss", { reason: "cooldown" }, attacker.id);
      return;
    }
    const d = distance(attacker.position, defender.position);
    if (d > this.config.stats.attackRange) {
      this.setActionOutcome(attacker, {
        action: "ATTACK",
        ok: false,
        code: "out-of-range",
        detail: `ATTACK failed: too far (dist=${d.toFixed(1)}, need ≤${this.config.stats.attackRange}). MOVE closer.`,
        distance: d,
      });
      this.emit("agent:attack:miss", { reason: "out-of-range", distance: d }, attacker.id);
      return;
    }

    attacker.attackReadyAt = this._tick + this.config.stats.attackCooldownTicks;
    const defending = defender.isDefending && this._tick < defender.defendingUntil;
    const raw = this.config.stats.attackDamage;
    const damage = Math.max(1, Math.round(raw * (defending ? 1 - ARENA.defendDamageReduction : 1)));
    defender.hp = Math.max(0, defender.hp - damage);
    attacker.behavioural.attacksLanded++;
    attacker.behavioural.damageDealt += damage;
    defender.behavioural.damageTaken += damage;

    // Knockback: pushes the defender AWAY from the attacker, interrupting channels.
    // Direction must be from attacker → defender; the old from→attacker vector
    // pulled them together and left fights stuck in a melee clump.
    defender.knockbackUntil = this._tick + this.config.stats.knockbackTicks;
    defender.knockbackFrom = attacker.position;
    defender.knockbackSpeedMul = this.config.stats.knockbackSpeedMul;
    this.interruptCollect(defender, "hit");

    // Soft recoil on the attacker so a wall-pinned defender still creates
    // separation — otherwise edge/corner clumps never break melee range.
    const recoilTicks = Math.max(2, Math.ceil(this.config.stats.knockbackTicks * 0.5));
    attacker.knockbackUntil = Math.max(attacker.knockbackUntil, this._tick + recoilTicks);
    attacker.knockbackFrom = defender.position;
    attacker.knockbackSpeedMul = this.config.stats.knockbackSpeedMul * 0.55;

    // Positions are included so a renderer can place impact effects without
    // having to reconstruct the world state at this tick.
    this.setActionOutcome(attacker, {
      action: "ATTACK",
      ok: true,
      code: "hit",
      detail: `ATTACK hit for ${damage} damage${defending ? " (partially blocked)" : ""}.`,
      distance: d,
    });
    this.emit("agent:attack", { damage, defending, position: attacker.position }, attacker.id);
    this.emit(
      "agent:hit",
      { damage, from: attacker.id, hp: defender.hp, position: defender.position },
      defender.id,
    );
    this.emit(
      "agent:knockback",
      { from: attacker.position, position: defender.position },
      defender.id,
    );

    if (defender.hp <= 0) {
      this.eliminate(defender, attacker.id);
    }
  }

  private resolveFlee(self: CricketState, opponent: CricketState): void {
    const arena = this.config.arenaSize;
    let threat: Vec2;
    if (opponent.alive) {
      threat = opponent.position;
    } else if (self.lastOpponentPosition) {
      threat = self.lastOpponentPosition;
    } else {
      // No intel: treat the centre as the threat so we break toward an edge.
      threat = [arena / 2, arena / 2];
    }
    const sprintDist =
      this.config.stats.speed * ARENA.fleeSpeedMul * ARENA.fleeDurationTicks;

    // Mid-sprint re-FLEE used to retarget every decision beat. On a wall the two
    // lateral slides score nearly equally, so the cricket ping-ponged in place.
    // Keep the current escape heading and just refresh the sprint timer.
    if (
      self.fleeUntil > this._tick &&
      self.moveTarget &&
      distance(self.position, self.moveTarget) > 1
    ) {
      self.fleeUntil = this._tick + ARENA.fleeDurationTicks;
      self.isDefending = false;
      this.interruptCollect(self, "flee");
      this.emit(
        "agent:flee",
        { target: self.moveTarget, durationTicks: ARENA.fleeDurationTicks },
        self.id,
      );
      return;
    }

    // Prefer away-from-threat, but never accept a clamp that cancels the sprint
    // (edge/corner pin previously left moveTarget === position → stuck forever).
    self.moveTarget = pickFleeTarget(self.position, threat, arena, sprintDist);
    self.fleeUntil = this._tick + ARENA.fleeDurationTicks;
    self.isDefending = false;
    this.interruptCollect(self, "flee");
    self.behavioural.retreatTicks += ARENA.fleeDurationTicks;
    this.emit(
      "agent:flee",
      { target: self.moveTarget, durationTicks: ARENA.fleeDurationTicks },
      self.id,
    );
  }

  private resolveExchange(self: CricketState, item: ItemKind): void {
    const cost = ARENA.exchangeCost[item];
    if (self.score < cost) {
      this.emit("agent:exchange", { item, ok: false, reason: "insufficient-score", cost }, self.id);
      return;
    }
    self.score -= cost;
    if (item === "bomb") self.bombs += 1;
    else self.blades += 1;
    this.emit("agent:exchange", { item, ok: true, cost, score: self.score }, self.id);
    this.emit("score", { agentId: self.id, delta: -cost, reason: "exchange", score: self.score }, self.id);
  }

  private resolveUse(self: CricketState, opponent: CricketState, item: ItemKind): void {
    if (item === "bomb") {
      if (self.bombs <= 0) {
        this.emit("agent:use", { item, ok: false, reason: "empty" }, self.id);
        return;
      }
      self.bombs -= 1;
      this.emit("agent:use", { item, ok: true, bombs: self.bombs }, self.id);
      this.detonateBomb(self, opponent);
      return;
    }
    if (self.blades <= 0) {
      this.emit("agent:use", { item, ok: false, reason: "empty" }, self.id);
      return;
    }
    self.blades -= 1;
    self.bladeUntil = this._tick + ARENA.bladeDurationTicks;
    this.emit("agent:use", { item, ok: true, blades: self.blades, until: self.bladeUntil }, self.id);
    this.emit(
      "item:blade",
      { position: self.position, durationTicks: ARENA.bladeDurationTicks },
      self.id,
    );
  }

  private detonateBomb(user: CricketState, opponent: CricketState): void {
    this.emit(
      "item:bomb",
      { position: user.position, radius: ARENA.bombRadius, damage: ARENA.bombDamage },
      user.id,
    );
    if (!opponent.alive) return;
    const d = distance(user.position, opponent.position);
    if (d > ARENA.bombRadius) return;

    const defending = opponent.isDefending && this._tick < opponent.defendingUntil;
    const damage = Math.max(
      1,
      Math.round(ARENA.bombDamage * (defending ? 1 - ARENA.defendDamageReduction : 1)),
    );
    opponent.hp = Math.max(0, opponent.hp - damage);
    user.behavioural.damageDealt += damage;
    opponent.behavioural.damageTaken += damage;
    opponent.knockbackUntil = this._tick + ARENA.bombKnockbackTicks;
    opponent.knockbackFrom = user.position;
    opponent.knockbackSpeedMul = this.config.stats.knockbackSpeedMul * 1.2;
    this.interruptCollect(opponent, "bomb");
    this.emit(
      "agent:hit",
      { damage, from: user.id, hp: opponent.hp, position: opponent.position, via: "bomb" },
      opponent.id,
    );
    this.emit(
      "agent:knockback",
      { from: user.position, position: opponent.position, via: "bomb" },
      opponent.id,
    );
    if (opponent.hp <= 0) this.eliminate(opponent, user.id);
  }

  private eliminate(victim: CricketState, by: AgentId | "trap"): void {
    victim.alive = false;
    victim.eliminated = true;
    this.emit("agent:eliminated", { by }, victim.id);
    if (by !== "trap") {
      const killer = this.crickets.find((c) => c.id === by);
      if (killer) {
        killer.score += ARENA.eliminationBonus;
        this.emit(
          "score",
          {
            agentId: killer.id,
            delta: ARENA.eliminationBonus,
            reason: "elimination",
            score: killer.score,
          },
          killer.id,
        );
      }
      if (this.config.eliminationEndsMatch) this.finish(by, "elimination");
    } else if (this.config.eliminationEndsMatch) {
      this.finish(this.cricket(1 - victim.slot).id, "elimination");
    }
  }

  /* ---------------------------------------------------------------- */
  /* Tick                                                              */
  /* ---------------------------------------------------------------- */

  step(): void {
    if (this._finished) return;
    this._tick++;

    this.maintainResources();
    this.integrateMovement();
    this.advanceChannels();
    this.tickBladeAuras();
    this.detectTraps();
    this.triggerContacts();

    if (this._tick >= this.config.durationTicks) {
      this.finish(this.decideOnScore(), "timeout");
      return;
    }

    this.emit("match:tick", { tick: this._tick });
  }

  private integrateMovement(): void {
    const { speed } = this.config.stats;
    const arena = this.config.arenaSize;
    for (const self of this.crickets) {
      // An eliminated cricket stops moving entirely. Without this guard its
      // last moveTarget would keep dragging the corpse across the arena, since
      // decisions are refused once `alive` is false and the target never clears.
      if (!self.alive) continue;
      const before = self.position;
      const fleeing = self.fleeUntil > this._tick;
      const knockbacked = self.knockbackUntil > this._tick;
      const moveSpeed = fleeing ? speed * ARENA.fleeSpeedMul : speed;

      if (knockbacked) {
        // Wall-aware push: a naive away-vector clamps to a no-op on edges/
        // corners, which left melee clumps stuck for the whole match.
        self.position = displaceAway(
          self.position,
          self.knockbackFrom,
          speed * self.knockbackSpeedMul,
          arena,
        );
        // Cancel voluntary MOVE, but keep an active FLEE sprint destination —
        // wiping it left edge-pinned fleers standing still after being hit.
        if (!fleeing) self.moveTarget = null;
      } else if (self.moveTarget) {
        const toTarget = distance(self.position, self.moveTarget);
        if (toTarget <= moveSpeed) {
          self.position = self.moveTarget;
          self.moveTarget = null;
        } else {
          const dir = direction(self.position, self.moveTarget);
          self.position = [
            self.position[0] + dir[0] * moveSpeed,
            self.position[1] + dir[1] * moveSpeed,
          ];
        }
      }

      const travelled = distance(before, self.position);
      if (travelled > 0) {
        self.behavioural.distanceTravelled += travelled;
        const fromSpawn = distance(self.position, self.spawn);
        if (fromSpawn > self.behavioural.maxExplorationDistance) {
          self.behavioural.maxExplorationDistance = fromSpawn;
        }
      }

      // Standing still is not holding a stance forever.
      if (self.isDefending && this._tick >= self.defendingUntil) self.isDefending = false;
    }
  }

  /** Spinning-blade aura: continuous close-range damage while active. */
  private tickBladeAuras(): void {
    if (this._finished) return;
    for (const self of this.crickets) {
      if (!self.alive || self.bladeUntil <= this._tick) continue;
      const opponent = this.cricket(1 - self.slot);
      if (!opponent.alive) continue;
      const d = distance(self.position, opponent.position);
      if (d > ARENA.bladeRange) continue;

      const defending = opponent.isDefending && this._tick < opponent.defendingUntil;
      const damage = Math.max(
        1,
        Math.round(ARENA.bladeDamagePerTick * (defending ? 1 - ARENA.defendDamageReduction : 1)),
      );
      opponent.hp = Math.max(0, opponent.hp - damage);
      self.behavioural.damageDealt += damage;
      opponent.behavioural.damageTaken += damage;
      // Light continuous shove so blades also break clumps.
      opponent.knockbackUntil = Math.max(opponent.knockbackUntil, this._tick + 2);
      opponent.knockbackFrom = self.position;
      opponent.knockbackSpeedMul = this.config.stats.knockbackSpeedMul * 0.7;
      this.emit(
        "agent:hit",
        { damage, from: self.id, hp: opponent.hp, position: opponent.position, via: "blade" },
        opponent.id,
      );
      if (opponent.hp <= 0) {
        this.eliminate(opponent, self.id);
        if (this._finished) return;
      }
    }
  }

  private advanceChannels(): void {
    for (const self of this.crickets) {
      if (self.collectingId === null) continue;
      if (self.knockbackUntil > this._tick) continue;
      if (this._tick < self.collectCompleteAt) continue;

      const resourceId = self.collectingId;
      const resource = this.resources.find((r) => r.id === resourceId);
      self.collectingId = null;
      if (!resource) {
        self.behavioural.collectionsFailed++;
        this.setActionOutcome(self, {
          action: "COLLECT",
          ok: false,
          code: "gone",
          detail: `COLLECT ${resourceId} failed: resource vanished before the channel finished.`,
          resourceId,
        });
        this.emit("agent:collect:miss", { reason: "gone", resourceId }, self.id);
        continue;
      }
      if (distance(self.position, resource.position) > this.config.stats.collectRange) {
        self.behavioural.collectionsFailed++;
        this.setActionOutcome(self, {
          action: "COLLECT",
          ok: false,
          code: "interrupted",
          detail: `COLLECT ${resourceId} interrupted: moved out of range before banking.`,
          resourceId,
        });
        this.emit("agent:collect:interrupted", { resourceId: resource.id }, self.id);
        continue;
      }
      // A trap that has already been identified is simply refused; one that has
      // not been identified goes off in your face.
      if (resource.kind === "trap") {
        self.behavioural.collectionsFailed++;
        this.setActionOutcome(self, {
          action: "COLLECT",
          ok: false,
          code: "trap",
          detail: `COLLECT ${resourceId} was a trap — you took the hit.`,
          resourceId,
        });
        this.triggerTrap(self, resource);
        continue;
      }
      this.bankResource(self, resource);
    }
  }

  /**
   * Landmine contact. A cricket that physically walks onto a trap sets it off.
   *
   * Note that identification does NOT disarm a trap. A landmine does not care
   * that you spotted it; it cares that you stepped on it. Detection exists to
   * give an agent *information*, and because detection typically fires only a
   * couple of ticks before contact, a cricket already committed to a suspicious
   * crystal usually cannot stop in time. That tension — "I can see it, can I
   * still get out?" — is the drama.
   *
   * The only reliable counter-play is SCOUT, which reveals traps from 12 units
   * out and therefore buys enough time to choose a different target.
   *
   * Runs after movement so that walking onto a trap and being knocked onto one
   * are handled identically.
   */
  private triggerContacts(): void {
    if (this._finished) return;
    for (const self of this.crickets) {
      if (!self.alive) continue;
      for (const r of [...this.resources]) {
        if (r.kind !== "trap") continue;
        if (distance(self.position, r.position) > ARENA.trapContactRadius) continue;
        if (self.collectingId === r.id) self.collectingId = null;
        this.triggerTrap(self, r);
        if (this._finished) return;
      }
    }
  }

  private bankResource(self: CricketState, resource: ResourceState): void {
    this.resources = this.resources.filter((r) => r.id !== resource.id);
    self.resourcesCollected++;
    self.behavioural.collectionsCompleted++;

    let delta = resource.value;
    if (resource.kind === "shield") {
      self.hp = Math.min(this.config.stats.maxHp, self.hp + SHIELD_HEAL);
    } else if (resource.kind === "bomb") {
      self.bombs += 1;
    } else if (resource.kind === "blade") {
      self.blades += 1;
    }
    self.score += delta;

    this.setActionOutcome(self, {
      action: "COLLECT",
      ok: true,
      code: "banked",
      detail: `COLLECT ${resource.id} banked (+${delta} ${resource.kind}). Score is now ${self.score}.`,
      resourceId: resource.id,
    });

    this.emit(
      "agent:collect:complete",
      {
        resourceId: resource.id,
        kind: resource.kind,
        value: delta,
        position: resource.position,
        bombs: self.bombs,
        blades: self.blades,
      },
      self.id,
    );
    this.emit("resource:collected", { resourceId: resource.id, kind: resource.kind }, self.id);
    this.emit("score", { agentId: self.id, delta, reason: resource.kind, score: self.score }, self.id);
  }

  private triggerTrap(self: CricketState, resource: ResourceState): void {
    this.resources = this.resources.filter((r) => r.id !== resource.id);
    const damage = ARENA.trapDamage;
    const penalty = ARENA.trapScorePenalty;
    self.hp = Math.max(0, self.hp - damage);
    self.score = Math.max(0, self.score - penalty);
    self.behavioural.trapsSteppedOn++;
    self.behavioural.damageTaken += damage;
    this.emit(
      "trap:triggered",
      { resourceId: resource.id, damage, scorePenalty: penalty, hp: self.hp, position: resource.position },
      self.id,
    );
    this.emit("score", { agentId: self.id, delta: -penalty, reason: "trap", score: self.score }, self.id);

    if (self.hp <= 0) {
      this.eliminate(self, "trap");
    }
  }

  /**
   * Passive trap sense. Keeps the world fair: a cricket is never killed by a
   * trap it was standing next to, so the drama comes from *walking into* one.
   */
  private detectTraps(): void {
    for (const self of this.crickets) {
      if (!self.alive) continue;
      for (const r of this.resources) {
        if (r.kind !== "trap" || self.knownTraps.has(r.id)) continue;
        if (distance(self.position, r.position) <= ARENA.trapDetectRadius) {
          self.knownTraps.add(r.id);
          r.revealed = true;
          self.behavioural.trapsDetected++;
          this.emit("trap:detected", { resourceId: r.id, via: "proximity" }, self.id);
          this.emit("resource:identified", { resourceId: r.id, kind: "trap" }, self.id);
        }
      }
    }
  }

  /**
   * The single place that decides a winner from state.
   *
   * Ranking score = crystal score + remaining HP. Attackers who chip the
   * opponent therefore climb the board even without a kill, and farmers who
   * ignore HP can still lose a crystal lead when the whistle blows.
   *
   * Used both at the final whistle and by `forceFinish`, so a match can never
   * end by two different rules depending on how it stopped.
   */
  private decideOnScore(): string | "draw" {
    const [a, b] = this.crickets as [CricketState, CricketState];
    const totalA = rankingTotal(a.score, a.hp);
    const totalB = rankingTotal(b.score, b.hp);
    if (totalA === totalB) {
      // A tie on the combined total is broken by survival, then by crystal score.
      if (a.alive !== b.alive) return a.alive ? a.id : b.id;
      if (a.score !== b.score) return a.score > b.score ? a.id : b.id;
      return "draw";
    }
    return totalA > totalB ? a.id : b.id;
  }

  private finish(winner: string | "draw", reason: "score" | "elimination" | "timeout" | "draw"): void {
    this._finished = true;
    this.winner = winner;
    this.winnerReason = reason;
    this.emit("match:end", {
      winner,
      reason,
      scores: this.crickets.map((c) => ({
        id: c.id,
        score: c.score,
        hp: c.hp,
        totalScore: rankingTotal(c.score, c.hp),
      })),
    });
  }

  /* ---------------------------------------------------------------- */
  /* Views                                                             */
  /* ---------------------------------------------------------------- */

  /** Force the match to conclude (used by watchdogs / aborted runs). */
  forceFinish(reason: "timeout" | "draw" = "timeout"): void {
    if (this._finished) return;
    const winner = this.decideOnScore();
    this.finish(winner, winner === "draw" ? "draw" : reason);
  }

  snapshot(options: SnapshotOptions = {}): RenderState {
    const revealTraps = options.revealTraps ?? true;
    return {
      tick: this._tick,
      timeRemainingTicks: this.timeRemainingTicks,
      finished: this._finished,
      crickets: this.crickets.map((c) => ({
        id: c.id,
        slot: c.slot,
        position: c.position,
        hp: c.hp,
        maxHp: this.config.stats.maxHp,
        score: c.score,
        totalScore: rankingTotal(c.score, c.hp),
        alive: c.alive,
        defending: c.isDefending,
        collecting: c.collectingId !== null,
        fleeing: c.fleeUntil > this._tick,
        bladeActive: c.bladeUntil > this._tick,
        bombs: c.bombs,
        blades: c.blades,
        action: c.currentAction,
      })),
      resources: this.resources.map((r) => {
        const known = revealTraps || r.revealed;
        return {
          id: r.id,
          kind: known ? r.kind : ("crystal" as ResourceKind),
          value: r.value,
          position: r.position,
          revealed: r.kind === "trap" && r.revealed,
        };
      }),
    };
  }

  /**
   * Per-decision behavioural sampling, called by the match driver.
   *
   * These measurements live in the engine rather than in the agent on purpose:
   * a personality card must describe what the cricket *did*, not what it says
   * about itself. An agent that lies in its `reason` field cannot inflate its
   * own aggression score.
   */
  sampleBehaviour(slot: number): void {
    const self = this.cricket(slot);
    const opponent = this.cricket(1 - slot);
    const obs = this.observe(slot);

    // Resource focus: is this cricket actually heading toward the nearest thing
    // worth taking, or is it wandering?
    const candidates = obs.resources.filter((r) => !r.identifiedAsTrap);
    if (candidates.length > 0 && self.moveTarget) {
      let nearest = candidates[0]!;
      for (const r of candidates) if (r.distance < nearest.distance) nearest = r;
      const toResource = unitVector(self.position, nearest.position);
      const toMove = unitVector(self.position, self.moveTarget);
      self.behavioural.resourceFocusSum +=
        toResource[0] * toMove[0] + toResource[1] * toMove[1];
      self.behavioural.resourceFocusSamples++;
    }

    // Retreat: moving away from a *visible* opponent. This is what gives the
    // "patience" trait something real to measure.
    if (opponent.alive && self.moveTarget) {
      const oppDistance = distance(self.position, opponent.position);
      if (oppDistance <= this.config.stats.visionRadius) {
        const distanceAfter = distance(self.moveTarget, opponent.position);
        if (distanceAfter > oppDistance + 0.5) self.behavioural.retreatTicks++;
      }
    }
  }

  getWinner(): string | "draw" {
    return this.winner;
  }

  getWinnerReason(): "score" | "elimination" | "timeout" | "draw" {
    return this.winnerReason;
  }

  getCricketResults() {
    return this.crickets.map((c) => ({
      id: c.id,
      score: c.score,
      hp: c.hp,
      totalScore: rankingTotal(c.score, c.hp),
      resourcesCollected: c.resourcesCollected,
      alive: c.alive,
      eliminated: c.eliminated,
      behavioural: c.behavioural,
    }));
  }

  getResources(): readonly RenderResource[] {
    return this.snapshot({ revealTraps: true }).resources;
  }

  /** Direct read used by tests and analysis tooling. */
  getCricketPosition(slot: number): Vec2 {
    return this.cricket(slot).position;
  }

  resourceCount(): number {
    return this.resources.length;
  }
}

function direction(from: Vec2, to: Vec2): Vec2 {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const len = Math.hypot(dx, dy);
  if (len === 0) return [0, 0];
  return [dx / len, dy / len];
}

function unitVector(from: Vec2, to: Vec2): Vec2 {
  return direction(from, to);
}

/**
 * Displace `from` by up to `step` units away from `threat`.
 *
 * When the direct away-vector is blocked by an arena edge/corner, clamp would
 * otherwise collapse the push to a no-op and melee clumps never separated.
 * Prefer the heading that still moves and increases separation (wall slide).
 */
function displaceAway(from: Vec2, threat: Vec2, step: number, arena: number): Vec2 {
  const preferred = direction(threat, from);
  const hasPreferred = preferred[0] !== 0 || preferred[1] !== 0;
  const dirs: Vec2[] = [];
  if (hasPreferred) {
    dirs.push(preferred);
    dirs.push([-preferred[1], preferred[0]]);
    dirs.push([preferred[1], -preferred[0]]);
  }
  const SQRT_HALF = Math.SQRT1_2;
  dirs.push(
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [SQRT_HALF, SQRT_HALF],
    [-SQRT_HALF, SQRT_HALF],
    [SQRT_HALF, -SQRT_HALF],
    [-SQRT_HALF, -SQRT_HALF],
  );

  const threatDist = distance(from, threat);
  let best: Vec2 = from;
  let bestScore = -Infinity;

  for (const dir of dirs) {
    if (dir[0] === 0 && dir[1] === 0) continue;
    const raw: Vec2 = [from[0] + dir[0] * step, from[1] + dir[1] * step];
    const clamped = clampToArena(raw, arena, ARENA.margin);
    const travel = distance(from, clamped);
    if (travel < 1e-6) continue;
    const sepGain = distance(clamped, threat) - threatDist;
    const awayDot = hasPreferred ? dir[0] * preferred[0] + dir[1] * preferred[1] : 0;
    const score = travel * 2 + sepGain * 4 + awayDot * travel;
    if (score > bestScore) {
      bestScore = score;
      best = clamped;
    }
  }
  return best;
}

/**
 * Choose a FLEE destination that actually displaces the cricket.
 *
 * A naive "away from threat × sprint" target clamps into the wall when the
 * cricket is already edge-pinned opposite the enemy, which made FLEE a no-op
 * and bots spam it forever while standing still. When that happens we slide
 * along the wall / toward an open corner instead of sprinting through the foe.
 */
function pickFleeTarget(
  from: Vec2,
  threat: Vec2,
  arena: number,
  sprintDist: number,
): Vec2 {
  const lo = ARENA.margin;
  const hi = arena - ARENA.margin;
  const corners: Vec2[] = [
    [lo, lo],
    [lo, hi],
    [hi, lo],
    [hi, hi],
  ];

  const preferred = direction(threat, from);
  const hasPreferred = preferred[0] !== 0 || preferred[1] !== 0;
  const dirs: Vec2[] = [];
  if (hasPreferred) {
    dirs.push(preferred);
    // Slide along the wall when the direct escape is blocked.
    dirs.push([-preferred[1], preferred[0]]);
    dirs.push([preferred[1], -preferred[0]]);
  }
  const SQRT_HALF = Math.SQRT1_2;
  dirs.push(
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [SQRT_HALF, SQRT_HALF],
    [-SQRT_HALF, SQRT_HALF],
    [SQRT_HALF, -SQRT_HALF],
    [-SQRT_HALF, -SQRT_HALF],
  );
  for (const corner of corners) {
    const toCorner = direction(from, corner);
    if (toCorner[0] !== 0 || toCorner[1] !== 0) dirs.push(toCorner);
  }

  const threatDist = distance(from, threat);
  let best: Vec2 = from;
  let bestScore = -Infinity;

  for (const dir of dirs) {
    if (dir[0] === 0 && dir[1] === 0) continue;
    const raw: Vec2 = [from[0] + dir[0] * sprintDist, from[1] + dir[1] * sprintDist];
    const clamped = clampToArena(raw, arena, ARENA.margin);
    const travel = distance(from, clamped);
    if (travel < 0.5) continue;
    const sepGain = distance(clamped, threat) - threatDist;
    // Positive = still fleeing away / sideways; negative = charging the threat.
    const awayDot = hasPreferred ? dir[0] * preferred[0] + dir[1] * preferred[1] : 0;
    // Midpoint proximity: punish paths that cut through the enemy.
    const mid: Vec2 = [(from[0] + clamped[0]) / 2, (from[1] + clamped[1]) / 2];
    const midClearance = distance(mid, threat) - Math.min(threatDist, 4);
    const score = travel * 2 + sepGain * 3 + awayDot * travel + midClearance * 2;
    if (score > bestScore) {
      bestScore = score;
      best = clamped;
    }
  }

  if (bestScore > -Infinity) return best;

  // Absolute fallback: sprint toward the open corner farthest from the threat.
  let farthest = corners[0]!;
  let farthestScore = -Infinity;
  for (const corner of corners) {
    const travel = distance(from, corner);
    if (travel < 0.5) continue;
    const score = distance(corner, threat) + travel;
    if (score > farthestScore) {
      farthestScore = score;
      farthest = corner;
    }
  }
  const toFar = distance(from, farthest);
  if (toFar <= sprintDist) return farthest;
  const dir = direction(from, farthest);
  return clampToArena(
    [from[0] + dir[0] * sprintDist, from[1] + dir[1] * sprintDist],
    arena,
    ARENA.margin,
  );
}

export { TICKS_PER_SECOND };
